//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, {
  createContext, useCallback, useContext, useState
} from 'react';
import {
  type Theme, ThemeProvider as MuiThemeProvider
} from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import { rocketGraphGlobalStyles, rocketGraphTheme } from './RocketGraphTheme';
import {
  COMPANY, LEGAL_COMPANY, PRODUCT_ABBREVIATION, PRODUCT_NAME,
  PRODUCT_SERVER_NAME
} from './RocketGraphConstants';

interface ThemeEntry {
  name: string;
  theme: Theme;
  globalStyles: Record<string, object>;
}

interface ThemeContextValue {
  themeKey: string;
  setThemeKey: (key: string) => void;
  themes: [string, ThemeEntry][];
  company: string;
  legalCompany: string;
  productName: string;
  productServerName: string;
  productAbbreviation: string;
  handleLogout: () => void;
}

const themes = new Map<string, ThemeEntry>([
  [
    'rocketGraph',
    {
      name: 'Rocket Graph',
      theme: rocketGraphTheme,
      globalStyles: rocketGraphGlobalStyles,
    },
  ],
]);

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [currentThemeKey, setCurrentThemeKey] = useState('rocketGraph');
  const [contextKey, setContextKey] = useState(0);

  // Stable: every API error handler in the tree memoizes on it, and a fresh
  // identity per render would rebuild all of them on each theme change.
  const handleLogout = useCallback(() => {
    // This will clear all context state because it is the top level context
    // provider. Change key to reset all providers.
    setContextKey((prevKey) => prevKey + 1);
    sessionStorage.clear();
  }, []);

  // Get the currently selected theme and global styles from the map.
  const fallback = themes.get(currentThemeKey) ??
                   themes.get('rocketGraph') ?? themes.values().next().value;
  if (!fallback)
    throw new Error('No themes registered');

  const { theme, globalStyles } = fallback;
  const productName = PRODUCT_NAME;
  const productServerName = PRODUCT_SERVER_NAME;
  const productAbbreviation = PRODUCT_ABBREVIATION;
  const company = COMPANY;
  const legalCompany = LEGAL_COMPANY;

  return (
    <ThemeContext.Provider
      key={contextKey}
      value={{
        themeKey: currentThemeKey,
        setThemeKey: setCurrentThemeKey,
        themes: Array.from(themes.entries()),
        company: company,
        legalCompany: legalCompany,
        productName: productName,
        productServerName: productServerName,
        productAbbreviation: productAbbreviation,
        handleLogout: handleLogout,
      }}
    >
      <MuiThemeProvider theme={theme}>
        <GlobalStyles styles={globalStyles} />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
